import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../utils/api'
import useMyPermission from '../../hooks/useMyPermission'
import NotificationGroups from '../../shared/components/NotificationGroups/NotificationGroups'
import {
    FiUsers, FiMapPin, FiClock, FiFilePlus, FiFileText, FiSearch,
    FiCheckSquare, FiCalendar, FiList, FiBookOpen, FiUser,
    FiActivity, FiAlertCircle, FiGrid, FiClipboard, FiSettings,
} from 'react-icons/fi'
import { BsDatabaseFillDown } from 'react-icons/bs'
import { ReactComponent as GeneratorIcon } from '../../shared/img/icon/generator.svg'
import './Home.css'

const STAT_CARDS = [
    { key: 'clients', label: 'Clientes ativos', to: '/client', icon: FiUsers, tone: 'blue' },
    { key: 'generators', label: 'Geradores ativos', to: '/generator', icon: GeneratorIcon, tone: 'indigo' },
    { key: 'serviceOrderOpen', label: 'OS em aberto', to: '/serviceOrderOpen', icon: FiFileText, tone: 'orange' },
    { key: 'serviceOrderReview', label: 'OS em análise', to: '/serviceOrderReview', icon: FiSearch, tone: 'purple' },
    { key: 'maintenanceOverdue', label: 'Manutenções vencidas', to: '/horimetro/list/all', icon: FiAlertCircle, tone: 'red' },
]

const OPERACIONAL_ITEMS = [
    { key: 'client_View', to: '/client', label: 'Clientes', icon: FiUsers },
    { key: 'myClient_View', to: '/myclient', label: 'Meus Clientes', icon: FiUser },
    { key: 'generator_View', to: '/generator', label: 'Geradores', icon: GeneratorIcon },
    { key: 'region_View', to: '/region/list', label: 'Regiões', icon: FiMapPin },
    { key: 'serviceExp_View', to: '/horimetro/list', label: 'Serviços e Prazos', icon: FiClock },
]

const SERVICE_ORDER_ITEMS = [
    { key: 'serviceOrder_View', to: '/serviceOrder', label: 'Cadastrar OS', icon: FiFilePlus },
    { key: 'serviceOrderOpen_View', to: '/serviceOrderOpen', label: 'Em Aberto', icon: FiFileText },
    { key: 'serviceOrderReview_View', to: '/serviceOrderReview', label: 'Em Análise', icon: FiSearch },
    { key: 'serviceOrderConcluded_View', to: '/serviceOrderConcluded', label: 'Concluídas', icon: FiCheckSquare },
    { key: 'schedule_View', to: '/maintenanceSchedule', label: 'Quadro de Manutenção', icon: FiCalendar },
    { key: 'checklistGroup_View', to: '/checklistGroup', label: 'Grupos de Checklist', icon: FiList },
    { key: 'checklistItem_View', to: '/checklistItem', label: 'Itens de Checklist', icon: FiCheckSquare },
    { key: 'procedure_View', to: '/procedure', label: 'Procedimentos', icon: FiBookOpen },
]

const ADMIN_ITEMS = [
    { key: 'historic_View', to: '/historic/list', label: 'Histórico', icon: FiActivity },
    { key: 'backup_View', to: '/backup', label: 'Backup', icon: BsDatabaseFillDown },
]

function greeting() {
    const hour = new Date().getHours()
    if (hour < 12) return 'Bom dia'
    if (hour < 18) return 'Boa tarde'
    return 'Boa noite'
}

function todayLabel() {
    const label = new Date().toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    return label.charAt(0).toUpperCase() + label.slice(1)
}

function Home() {
    const [token] = useState(localStorage.getItem('token') || '')
    const [summary, setSummary] = useState({})
    const [notification, setNotification] = useState({ count: 0, groups: [] })
    const [myPermission, setMyPermission] = useState({ group: '' })
    const [load, setLoad] = useState(false)
    const { checkPermission } = useMyPermission()

    const userName = localStorage.getItem('userName')
    const isAdminGroup = myPermission.group === 'DEV_MASTER' || myPermission.group === 'ADM_USER'

    useEffect(() => {
        api.get('/home/summary', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setSummary(response.data.summary || {})
            setMyPermission(response.data.permission)
            setLoad(true)
        })

        api.get('/notification/getall', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setNotification(response.data.notification)
        })
    }, [token])

    if (!load) {
        return <div className="pg-loading">Carregando...</div>
    }

    const visibleStats = STAT_CARDS.filter((card) => summary[card.key] !== undefined)

    const adminItems = [
        ...(isAdminGroup ? [{ key: 'admin-users', to: '/system-user/list', label: 'Usuários', icon: FiUsers }] : []),
        ...ADMIN_ITEMS.filter((item) => checkPermission(myPermission, item.key)),
    ]

    const shortcutSections = [
        { title: 'Operacional', tone: 'blue', sectionIcon: FiGrid, items: OPERACIONAL_ITEMS.filter((item) => checkPermission(myPermission, item.key)) },
        { title: 'Ordens de Serviço', tone: 'orange', sectionIcon: FiClipboard, items: SERVICE_ORDER_ITEMS.filter((item) => checkPermission(myPermission, item.key)) },
        { title: 'Administração', tone: 'purple', sectionIcon: FiSettings, items: adminItems },
    ].filter((section) => section.items.length > 0)

    return (
        <section className="pg-page">
            <div className="home-wrap">

                <div className="home-greeting">
                    <p className="home-greeting-title">{greeting()}, {userName}!</p>
                    <p className="home-greeting-sub">{todayLabel()}</p>
                </div>

                <div className="home-columns">
                    <div className="home-main">
                        {shortcutSections.map((section) => (
                            <div className="home-section" key={section.title}>
                                <p className={`home-section-title home-section-title-${section.tone}`}>
                                    <section.sectionIcon size={14} />
                                    {section.title}
                                </p>
                                <div className="home-shortcuts-grid">
                                    {section.items.map((item) => (
                                        <Link key={item.key} to={item.to} className={`home-shortcut-card home-shortcut-card-${section.tone}`}>
                                            <div className={`home-shortcut-icon home-shortcut-icon-${section.tone}`}><item.icon size={22} /></div>
                                            <span>{item.label}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="home-sidebar">
                        {notification.groups?.length > 0 && (
                            <div className="home-section">
                                <p className="home-section-title">Avisos</p>
                                <div className="home-alerts-card">
                                    <NotificationGroups groups={notification.groups} />
                                </div>
                            </div>
                        )}

                        {visibleStats.length > 0 && (
                            <div className="home-section">
                                <p className="home-section-title">Resumo</p>
                                <div className="home-stats-grid">
                                    {visibleStats.map((card) => (
                                        <Link key={card.key} to={card.to} className={`home-stat-card home-stat-card-${card.tone}`}>
                                            <div className={`home-stat-icon home-stat-icon-${card.tone}`}>
                                                <card.icon size={20} />
                                            </div>
                                            <div>
                                                <p className="home-stat-value">{summary[card.key]}</p>
                                                <p className="home-stat-label">{card.label}</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </section>
    )
}

export default Home
